import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { DocsLink } from '@/components/docs/docs-link'
import { cn } from '@/utils/cn'
import { useToolbar } from './ToolbarContext'

export type PageLayout =
  /** Flush, full-bleed — for table/graph/map pages whose content needs every pixel. */
  | 'full'
  /** Padded (px-6 py-6) wrapper — for card-based content pages. */
  | 'padded'

export interface PageGuide {
  /** Docs route to open. */
  to: string
  /** Pill label. Default: "Guide". */
  label?: string
  /** Hover popover text. */
  hint?: string
}

export interface PageShellProps {
  /** Layout family — see PageLayout. Default: "padded". */
  layout?: PageLayout
  /** Page title (rendered as a header block above content). */
  title?: string
  /**
   * One-line summary under the title. The "full" layout hides it below md,
   * where its header does not scroll away with the content.
   */
  summary?: string
  /** Optional leading icon for the title row. */
  icon?: LucideIcon
  /** Optional fully-custom hero. If provided, it replaces the title/summary block. */
  hero?: ReactNode
  /** Optional toolbar actions, registered into the top bar's right slot. */
  toolbar?: ReactNode
  /** Optional guide pill, registered next to the breadcrumb. */
  guide?: PageGuide
  /** Inner content. */
  children: ReactNode
  /** Escape hatch for extra classes on the outer wrapper. */
  className?: string
}

/**
 * Standard chrome for every main page.
 *
 *   - Registers the toolbar / guide pill into the top bar via context (no duplicate header strip).
 *   - Renders the page body in one of two layouts:
 *       "full"   = flush, fills the area (good for tables/graphs/maps).
 *       "padded" = padded wrapper, px-4 on the phone and px-6 from md (good for card-based pages).
 *   - Optionally renders a SubjectHero block above the content for detail/subject pages.
 */
export function PageShell({
  layout = 'padded',
  title,
  summary,
  icon,
  hero,
  toolbar,
  guide,
  children,
  className,
}: PageShellProps) {
  useToolbar(toolbar ?? null)

  const headerBlock =
    hero ??
    (title ? <PageHeading icon={icon} title={title} summary={summary} guide={guide} /> : null)

  // The header block uses identical padding across both layouts so every page's
  // chrome lines up. Only the *body* below the header differs:
  //   - "full"   = flush, no body padding (table/graph/map fills the area)
  //   - "padded" = px-6 with bottom padding and space-y-6 between sections
  if (layout === 'full') {
    return (
      <div className={cn('flex flex-1 min-h-0 flex-col overflow-hidden bg-background', className)}>
        {/* This header stays pinned above the content, so on the phone it drops the summary. */}
        {headerBlock && (
          <div className="shrink-0 p-4 max-md:[&_[data-slot=page-summary]]:hidden md:px-6 md:pt-6">
            {headerBlock}
          </div>
        )}
        <div className="min-h-0 flex-1 flex flex-col overflow-hidden">{children}</div>
      </div>
    )
  }

  return (
    <div className={cn('flex-1 overflow-y-auto bg-background', className)}>
      {headerBlock && <div className="p-4 md:px-6 md:pt-6">{headerBlock}</div>}
      <div className="px-4 pb-6 space-y-6 md:px-6">{children}</div>
    </div>
  )
}

/**
 * Default header block — leading icon, h1 title, one-line summary. Used by PageShell
 * when given a `title` prop; can also be rendered standalone if a page wants the same
 * styling outside of PageShell.
 */
export function PageHeading({
  icon: Icon,
  title,
  summary,
  guide,
}: {
  icon?: LucideIcon
  title: string
  summary?: string
  guide?: PageGuide
}) {
  return (
    <div className="flex items-start gap-3">
      {Icon && (
        <div className="h-9 w-9 rounded-lg border border-primary/40 bg-primary/5 flex items-center justify-center shrink-0 mt-0.5">
          <Icon className="h-5 w-5 text-primary" />
        </div>
      )}
      <div className="min-w-0">
        {/* As tall as the icon below md, so a title that stands alone lines up with it. */}
        <div className="flex items-center gap-2 flex-wrap max-md:min-h-9">
          <h1 className="text-xl font-semibold leading-tight text-foreground break-words md:truncate">
            {title}
          </h1>
          {guide && <DocsLink to={guide.to} label={guide.label ?? 'Guide'} hint={guide.hint} />}
        </div>
        {summary && (
          <p data-slot="page-summary" className="text-sm text-muted-foreground mt-1">
            {summary}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * Hero block for subject (detail) pages — a specific case, identity, schema, etc.
 * Never use on list/index pages; the breadcrumb already names them.
 */
export function SubjectHero({
  icon: Icon,
  title,
  subtitle,
  meta,
}: {
  icon: LucideIcon
  title: string
  subtitle?: string
  meta?: ReactNode
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 rounded-lg border border-primary/40 bg-primary/5 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div className="min-w-0">
        <h1 className="text-xl font-bold leading-tight text-foreground break-words md:truncate md:text-2xl">
          {title}
        </h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
        {meta && <div className="mt-2">{meta}</div>}
      </div>
    </div>
  )
}
